import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatValue } from "../lib/format";

export interface Band {
  from: number;
  to: number;
  color: string;
  label: string;
}

export interface Point {
  x: number;
  y: number;
  /** Shown as a third line in the tooltip — a station name, a depth class. */
  note?: string;
}

export interface Series {
  key: string;
  label: string;
  color: string;
  kind: "line" | "bar" | "scatter";
  points: Point[];
}

interface Props {
  series: Series[];
  /** Background classification bands, drawn behind everything and labelled at
   *  the right edge — VSM's A–F grades, VLM's trophic classes. */
  bands?: Band[];
  /** Dashed reference lines with labels — published standards. */
  thresholds?: { value: number; label: string; color: string }[];
  yLabel: string;
  /** Formats a value for ticks and tooltips. */
  format?: (v: number) => string;
  /** Force a y-domain; otherwise derived from the data and any bands. */
  yDomain?: [number, number];
  /** x values are years; formatted as integers rather than dates. */
  xIsYear?: boolean;
  emptyMessage?: string;
}

const PAD = { top: 14, right: 16, bottom: 30, left: 52 };
const BAND_LABEL_W = 62;

function niceTicks(lo: number, hi: number, count = 5): number[] {
  if (!Number.isFinite(lo) || !Number.isFinite(hi) || hi <= lo) return [lo];
  const raw = (hi - lo) / count;
  const mag = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / mag;
  const step = (norm >= 7.5 ? 10 : norm >= 3.5 ? 5 : norm >= 1.5 ? 2 : 1) * mag;
  const out: number[] = [];
  for (let v = Math.ceil(lo / step) * step; v <= hi + step * 1e-9; v += step) {
    out.push(Number(v.toFixed(10)));
  }
  return out;
}

export default function TrendChart({
  series, bands = [], thresholds = [], yLabel, format = formatValue,
  yDomain, xIsYear = true, emptyMessage = "No readings here yet",
}: Props) {
  const wrap = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 720, h: 260 });
  const [hover, setHover] = useState<{ p: Point; s: Series; px: number; py: number } | null>(null);

  /*
   * The tooltip is measured rather than estimated. Its height changes with
   * content (two lines, or three when a point carries a note) and its width
   * with the label, and both are needed to keep it inside the plot area —
   * see `tipPos` below. Measuring in a layout effect means the corrected
   * position is applied before the browser paints, so there is no flicker.
   */
  const tipRef = useRef<HTMLDivElement>(null);
  const [tip, setTip] = useState({ w: 150, h: 58 });

  useLayoutEffect(() => {
    const el = tipRef.current;
    if (!el) return;
    const { offsetWidth: w, offsetHeight: h } = el;
    setTip((prev) => (prev.w === w && prev.h === h ? prev : { w, h }));
  }, [hover]);

  useEffect(() => {
    const el = wrap.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect;
      setSize({ w: Math.max(260, width), h: Math.max(150, height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const all = useMemo(() => series.flatMap((s) => s.points), [series]);
  const hasBandLabels = bands.length > 0;
  const rightPad = PAD.right + (hasBandLabels ? BAND_LABEL_W : 0);

  const geom = useMemo(() => {
    const innerW = Math.max(10, size.w - PAD.left - rightPad);
    const innerH = Math.max(10, size.h - PAD.top - PAD.bottom);

    const xs = all.map((p) => p.x);
    let x0 = xs.length ? Math.min(...xs) : 0;
    let x1 = xs.length ? Math.max(...xs) : 1;
    if (x0 === x1) { x0 -= 1; x1 += 1; }

    const ys = all.map((p) => p.y);
    let y0: number, y1: number;
    if (yDomain) {
      [y0, y1] = yDomain;
    } else {
      const bandLo = bands.length ? Math.min(...bands.map((b) => b.from)) : Infinity;
      const bandHi = bands.length ? Math.max(...bands.map((b) => b.to)) : -Infinity;

      /*
       * A threshold only widens the scale when it is anywhere near the data.
       * Chloride here runs 13–18 mg/L against a 250 mg/L standard: including
       * it would flatten every reading onto the axis to show a line nothing
       * approaches. Far-off thresholds are dropped from the domain and
       * reported underneath the chart instead, which is the honest reading —
       * "nowhere near the standard" — without destroying the trend.
       */
      const dLo = ys.length ? Math.min(...ys) : 0;
      const dHi = ys.length ? Math.max(...ys) : 1;
      const dSpan = (dHi - dLo) || Math.abs(dHi) || 1;
      const near = (v: number) => v >= dLo - dSpan * 1.5 && v <= dHi + dSpan * 1.5;
      const shown = thresholds.filter((t) => near(t.value));
      const thrLo = shown.length ? Math.min(...shown.map((t) => t.value)) : Infinity;
      const thrHi = shown.length ? Math.max(...shown.map((t) => t.value)) : -Infinity;

      const lo = Math.min(dLo, bandLo, thrLo);
      const hi = Math.max(dHi, bandHi, thrHi);
      const span = hi - lo || Math.abs(hi) || 1;
      // Bands already define the meaningful extent; padding them adds dead space.
      y0 = bands.length ? lo : lo - span * 0.08;
      y1 = bands.length ? hi : hi + span * 0.08;
      if (!bands.length && lo >= 0 && y0 < 0) y0 = 0;
    }
    if (y1 <= y0) y1 = y0 + 1;

    const xOf = (x: number) => PAD.left + ((x - x0) / (x1 - x0)) * innerW;
    const yOf = (y: number) => PAD.top + innerH - ((y - y0) / (y1 - y0)) * innerH;

    return { xOf, yOf, x0, x1, y0, y1, innerW, innerH };
  }, [all, size, bands, thresholds, yDomain, rightPad]);

  /** Thresholds that fall inside the drawn scale; the rest are reported in
   *  words beneath the chart rather than crushing it. */
  const visibleThresholds = thresholds.filter(
    (t) => t.value >= geom.y0 && t.value <= geom.y1,
  );
  const offScale = thresholds.filter((t) => !visibleThresholds.includes(t));

  const { xOf, yOf, x0, x1, y0, y1 } = geom;

  const yTicks = useMemo(
    () => niceTicks(y0, y1, size.h < 200 ? 4 : 5),
    [y0, y1, size.h],
  );

  const xTicks = useMemo(() => {
    const span = x1 - x0;
    const maxTicks = Math.max(2, Math.floor(geom.innerW / (xIsYear ? 52 : 72)));
    if (xIsYear) {
      const step = Math.max(1, Math.ceil((span + 1) / maxTicks));
      const out: number[] = [];
      for (let y = Math.ceil(x0 / step) * step; y <= x1; y += step) out.push(y);
      return out.length ? out : [Math.round(x0)];
    }
    return niceTicks(x0, x1, maxTicks);
  }, [x0, x1, geom.innerW, xIsYear]);

  function onMove(e: React.MouseEvent<SVGSVGElement>) {
    const box = e.currentTarget.getBoundingClientRect();
    const mx = e.clientX - box.left, my = e.clientY - box.top;
    let best: typeof hover = null, bestD = Infinity;
    for (const s of series) {
      for (const p of s.points) {
        const px = xOf(p.x), py = yOf(p.y);
        const d = (px - mx) ** 2 + (py - my) ** 2;
        if (d < bestD) { bestD = d; best = { p, s, px, py }; }
      }
    }
    setHover(bestD < 44 * 44 ? best : null);
  }

  /*
   * Where to put the tooltip so it stays wholly inside the chart.
   *
   * It used to sit above the point unconditionally, via a -100% Y transform.
   * For a point near the top of the scale that put the whole box above the
   * chart's own box, where `overflow: hidden` clipped it and it read as
   * sliding behind the metric ribbon above. So: prefer above, fall back to
   * below, and only if neither fits clamp it alongside the point.
   */
  const tipPos = (() => {
    if (!hover) return null;
    const GAP = 12;
    const EDGE = 2;
    const above = hover.py - GAP - tip.h;
    const below = hover.py + GAP;

    let top: number;
    if (above >= EDGE) top = above;
    else if (below + tip.h <= size.h - EDGE) top = below;
    else top = Math.min(Math.max(EDGE, hover.py - tip.h / 2), size.h - tip.h - EDGE);

    // Clamp horizontally against the measured width rather than a guess, so a
    // long label ("mean of 7 stations") cannot hang off either edge.
    const half = tip.w / 2;
    const left = Math.min(Math.max(hover.px, half + EDGE), Math.max(half + EDGE, size.w - half - EDGE));
    return { left, top };
  })();

  const empty = all.length === 0;
  const barSeries = series.filter((s) => s.kind === "bar");
  // Bars share the x-slot, so each needs a share of it rather than the whole.
  const slot = barSeries.length
    ? Math.max(3, (geom.innerW / Math.max(1, (x1 - x0) + 1)) * 0.74)
    : 0;
  const barW = barSeries.length ? Math.max(2, slot / barSeries.length) : 0;

  return (
    <div className="chart" ref={wrap}>
      <svg
        width={size.w}
        height={size.h}
        role="img"
        aria-label={`${yLabel} over time`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        {/* Classification bands, behind everything. */}
        {bands.map((b) => {
          const top = yOf(Math.min(b.to, y1));
          const bot = yOf(Math.max(b.from, y0));
          if (bot <= top) return null;
          return (
            <g key={b.label}>
              <rect
                x={PAD.left} y={top}
                width={geom.innerW} height={bot - top}
                fill={b.color} opacity={0.5}
              />
              <text
                className="band-label"
                x={PAD.left + geom.innerW + 7}
                y={(top + bot) / 2}
                dominantBaseline="middle"
              >
                {b.label}
              </text>
            </g>
          );
        })}

        {yTicks.map((v) => (
          <g key={"y" + v}>
            <line className="grid" x1={PAD.left} x2={PAD.left + geom.innerW} y1={yOf(v)} y2={yOf(v)} />
            <text className="tick" x={PAD.left - 8} y={yOf(v)} textAnchor="end" dominantBaseline="middle">
              {format(v)}
            </text>
          </g>
        ))}

        {xTicks.map((v) => (
          <text key={"x" + v} className="tick" x={xOf(v)} y={size.h - 10} textAnchor="middle">
            {xIsYear ? String(Math.round(v)) : format(v)}
          </text>
        ))}

        {visibleThresholds.map((t) => (
          <g key={t.label}>
            <line
              className="threshold"
              x1={PAD.left} x2={PAD.left + geom.innerW}
              y1={yOf(t.value)} y2={yOf(t.value)}
              stroke={t.color}
            />
            <text
              className="threshold-label"
              x={PAD.left + geom.innerW} y={yOf(t.value) - 5}
              textAnchor="end" fill={t.color}
            >
              {t.label}
            </text>
          </g>
        ))}

        {series.map((s, si) => {
          if (s.kind === "bar") {
            const bi = barSeries.indexOf(s);
            return (
              <g key={s.key}>
                {s.points.map((p, i) => {
                  const h = Math.max(0, yOf(Math.max(y0, 0)) - yOf(p.y));
                  return (
                    <rect
                      key={i}
                      x={xOf(p.x) - slot / 2 + bi * barW}
                      y={yOf(p.y)}
                      width={barW}
                      height={h}
                      fill={s.color}
                      opacity={hover && hover.p === p ? 1 : 0.85}
                    />
                  );
                })}
              </g>
            );
          }
          const sorted = [...s.points].sort((a, b) => a.x - b.x);
          const d = sorted.map((p, i) => `${i ? "L" : "M"}${xOf(p.x)},${yOf(p.y)}`).join(" ");
          return (
            <g key={s.key}>
              {s.kind === "line" && sorted.length > 1 && (
                <path className="line" d={d} stroke={s.color} fill="none" />
              )}
              {sorted.map((p, i) => (
                <circle
                  key={i}
                  cx={xOf(p.x)} cy={yOf(p.y)}
                  r={s.kind === "scatter" ? 3 : 3.4}
                  fill={s.color}
                  className={"pt" + (hover && hover.p === p ? " pt-hot" : "")}
                  opacity={s.kind === "scatter" ? 0.62 : 1}
                />
              ))}
            </g>
          );
        })}

        {hover && (
          <>
            <line
              className="crosshair"
              x1={hover.px} x2={hover.px}
              y1={PAD.top} y2={PAD.top + geom.innerH}
            />
            <circle className="pt-ring" cx={hover.px} cy={hover.py} r={6.5} stroke={hover.s.color} />
          </>
        )}

        {empty && (
          <text className="empty" x={size.w / 2} y={size.h / 2} textAnchor="middle">
            {emptyMessage}
          </text>
        )}
      </svg>

      {offScale.length > 0 && (
        <div className="chart-note">
          {offScale.map((t) => t.label).join(" and ")}{" "}
          {offScale.length === 1 ? "is" : "are"} outside this range — every reading shown is{" "}
          {offScale.every((t) => t.value > geom.y1) ? "well below" : "well outside"} it.
        </div>
      )}

      {hover && (
        <div
          className="tooltip"
          ref={tipRef}
          style={{ left: tipPos?.left, top: tipPos?.top }}
        >
          <div className="tt-value">
            {format(hover.p.y)} <span>{yLabel}</span>
          </div>
          <div className="tt-meta">
            {xIsYear ? Math.round(hover.p.x) : format(hover.p.x)}
            {series.length > 1 ? ` · ${hover.s.label}` : ""}
          </div>
          {hover.p.note && <div className="tt-meta tt-note-line">{hover.p.note}</div>}
        </div>
      )}
    </div>
  );
}
